import * as B from '@blueprintjs/core';
import {useVirtualizer} from '@tanstack/react-virtual';
import React from 'react';
import {useNavigate} from 'react-router-dom';
import {TableInstance} from 'react-table';

import {ApiProjectReport} from 'app/modules/Remote/Feature';
import * as routeUtils from 'app/utils/routeUtils';

import cs from '../PropertyOverview.module.scss';
import {TableHeader} from '../PropertyTable';
import {VirtualizedTableRow} from '../VirtualizedTableRow';
import {MAX_SELECTED_REPORTS} from './reportsTableState';

export type ReportsStatus = 'loading' | 'error' | 'done';

// selection, document title, type, property, author, created, updated
export const GRID_TEMPLATE_COLUMNS =
  '5.6rem minmax(16rem, 2fr) minmax(8rem, 0.6fr) minmax(14rem, 1.5fr) minmax(10rem, 1fr) minmax(9rem, 1fr) minmax(12rem, 1fr)';

const PortfolioReportsTable: React.FunctionComponent<{
  table: TableInstance<ApiProjectReport>;
  status: ReportsStatus;
  hasReports: boolean;
  organizationId: string;
  projectId: string;
  onExportSelected: () => void;
  /** Omitted when the user's role doesn't allow deleting reports. */
  onDeleteSelected?: () => void;
}> = ({
  table,
  status,
  hasReports,
  organizationId,
  projectId,
  onExportSelected,
  onDeleteSelected,
}) => {
  const tableContainerRef = React.useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  const rowVirtualizer = useVirtualizer({
    count: table.rows.length,
    estimateSize: () => 56,
    getScrollElement: () => tableContainerRef.current,
    getItemKey: (index) => table.rows[index].id,
  });

  const openReport = React.useCallback(
    (report: ApiProjectReport) =>
      navigate(
        routeUtils.makeSavedReportUrl(organizationId, projectId, report.feature.lensId, report.id)
      ),
    [navigate, organizationId, projectId]
  );

  const selectedCount = table.selectedFlatRows.length;

  if (status === 'loading') {
    return (
      <div className={cs.tableContainer}>
        <B.Spinner className={cs.tableLoadingSpinner} />
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className={cs.tableContainer}>
        <B.NonIdealState
          icon="error"
          description="There was an error loading reports. Please refresh and try again."
        />
      </div>
    );
  }

  return (
    <div className={cs.tableContainer} ref={tableContainerRef}>
      <table>
        <thead>
          <TableHeader table={table} gridTemplateColumns={GRID_TEMPLATE_COLUMNS} />
          {selectedCount > 0 && (
            <tr
              className={cs.selectedRowActionsContainer}
              style={{gridTemplateColumns: GRID_TEMPLATE_COLUMNS}}
            >
              <th style={{padding: 0, width: '100vw'}}>
                <SelectedReportsActions
                  count={selectedCount}
                  onExport={onExportSelected}
                  onDelete={onDeleteSelected}
                  onClear={() => table.toggleAllRowsSelected(false)}
                />
              </th>
            </tr>
          )}
        </thead>
        <tbody style={{height: `${rowVirtualizer.getTotalSize()}px`}}>
          {rowVirtualizer.getVirtualItems().map((virtualRow) => {
            const row = table.rows[virtualRow.index];
            table.prepareRow(row);
            return (
              <VirtualizedTableRow
                key={row.id}
                row={row}
                offset={virtualRow.start}
                gridTemplateColumns={GRID_TEMPLATE_COLUMNS}
                onOpen={openReport}
              />
            );
          })}
        </tbody>
      </table>
      {table.rows.length === 0 && (
        <div className={cs.emptyListMessage}>
          {hasReports ? (
            <>
              No reports match these filters.
              <br />
              <a
                onClick={(ev) => {
                  ev.preventDefault();
                  table.setAllFilters([]);
                }}
              >
                Show all reports
              </a>
            </>
          ) : (
            'No saved reports in this portfolio yet.'
          )}
        </div>
      )}
    </div>
  );
};

const SelectedReportsActions: React.FunctionComponent<{
  count: number;
  onExport: () => void;
  onDelete?: () => void;
  onClear: () => void;
}> = ({count, onExport, onDelete, onClear}) => {
  const isOverLimit = count > MAX_SELECTED_REPORTS;
  const overLimitMessage = `Select up to ${MAX_SELECTED_REPORTS} documents at a time`;

  return (
    <div className={cs.selectedRowActions}>
      <div className={cs.selectedRowSummary}>
        <strong>{count === 1 ? '1 document selected' : `${count} documents selected`}</strong> –{' '}
        <a
          onClick={(ev) => {
            ev.preventDefault();
            onClear();
          }}
        >
          Clear selection
        </a>
      </div>
      <div className={cs.selectedRowButtons}>
        <B.Tooltip content={overLimitMessage} disabled={!isOverLimit}>
          <B.AnchorButton
            icon="document"
            text="Export PDFs"
            disabled={isOverLimit}
            onClick={onExport}
          />
        </B.Tooltip>
        {onDelete && (
          <B.Tooltip content={overLimitMessage} disabled={!isOverLimit}>
            <B.AnchorButton icon="trash" text="Delete" disabled={isOverLimit} onClick={onDelete} />
          </B.Tooltip>
        )}
      </div>
    </div>
  );
};

export default PortfolioReportsTable;
